import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Upload, Search, FolderOpen, Image, Video, Trash2, Copy, Check, X,
  Loader2, Grid, List, ChevronDown, MoreVertical, Edit2, Download, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatFileSize = (bytes) => {
  if (bytes === null || bytes === undefined || isNaN(bytes) || bytes <= 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const MediaLibrary = ({ onSelect, selectMode = false }) => {
  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [editingAsset, setEditingAsset] = useState(null);
  const [stats, setStats] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const fileInputRef = useRef(null);

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedFolder) params.append('folder', selectedFolder);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetch(`${API_URL}/api/media?${params}`);
      if (response.ok) {
        const data = await response.json();
        setAssets(data.items);
      }
    } catch (error) {
      console.error('Failed to fetch assets:', error);
      toast.error('Failed to load media assets');
    } finally {
      setLoading(false);
    }
  }, [selectedFolder, searchQuery]);

  const fetchFolders = async () => {
    try {
      const response = await fetch(`${API_URL}/api/media/folders`);
      if (response.ok) {
        const data = await response.json();
        setFolders(data);
      }
    } catch (error) {
      console.error('Failed to fetch folders:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/media/stats/summary`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchAssets();
    fetchFolders();
    fetchStats();
  }, [fetchAssets]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    let successCount = 0;
    let failCount = 0;

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', selectedFolder || 'general');

      try {
        const response = await fetch(`${API_URL}/api/media/upload`, {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          successCount++;
        } else {
          const error = await response.json();
          console.error('Upload failed:', error);
          failCount++;
        }
      } catch (error) {
        console.error('Upload error:', error);
        failCount++;
      }
    }

    setUploading(false);
    fileInputRef.current.value = '';

    if (successCount > 0) {
      toast.success(`Uploaded ${successCount} file${successCount > 1 ? 's' : ''}`);
      fetchAssets();
      fetchFolders();
      fetchStats();
    }
    if (failCount > 0) {
      toast.error(`Failed to upload ${failCount} file${failCount > 1 ? 's' : ''}`);
    }
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      const response = await fetch(`${API_URL}/api/media/${assetId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('File deleted');
        fetchAssets();
        fetchFolders();
        fetchStats();
      } else {
        toast.error('Failed to delete file');
      }
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedAssets.length === 0) return;
    if (!window.confirm(`Delete ${selectedAssets.length} selected files?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/media`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedAssets })
      });

      if (response.ok) {
        toast.success(`Deleted ${selectedAssets.length} files`);
        setSelectedAssets([]);
        fetchAssets();
        fetchFolders();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to delete files');
    }
  };

  const handleCopyUrl = (asset) => {
    const fullUrl = `${window.location.origin}${asset.url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(asset.id);
    toast.success('URL copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleUpdateAsset = async (assetId, updates) => {
    try {
      const params = new URLSearchParams(updates);
      const response = await fetch(`${API_URL}/api/media/${assetId}?${params}`, {
        method: 'PUT'
      });

      if (response.ok) {
        toast.success('Asset updated');
        setEditingAsset(null);
        fetchAssets();
        fetchFolders();
      } else {
        toast.error('Failed to update asset');
      }
    } catch (error) {
      toast.error('Failed to update asset');
    }
  };

  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev => 
      prev.includes(assetId) 
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  const handleSelectAsset = (asset) => {
    if (selectMode && onSelect) {
      onSelect(asset);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="py-4">
              <div className="text-2xl font-bold text-[#00457f]">{stats.total_assets}</div>
              <div className="text-sm text-gray-500">Total Assets</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="text-2xl font-bold text-[#00457f]">{formatFileSize(stats.total_size)}</div>
              <div className="text-sm text-gray-500">Total Size</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="text-2xl font-bold text-[#00457f]">{stats.by_type?.image?.count || 0}</div>
              <div className="text-sm text-gray-500">Images</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="text-2xl font-bold text-[#00457f]">{stats.by_type?.video?.count || 0}</div>
              <div className="text-sm text-gray-500">Videos</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="flex gap-2 items-center">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleUpload}
            className="hidden"
          />
          <Button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-[#00457f] hover:bg-[#003461]"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Upload Files
          </Button>
          
          {selectedAssets.length > 0 && (
            <Button variant="destructive" onClick={handleBulkDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete ({selectedAssets.length})
            </Button>
          )}
        </div>

        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
          
          <div className="flex border rounded-md">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${viewMode === 'grid' ? 'bg-gray-100' : ''}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 ${viewMode === 'list' ? 'bg-gray-100' : ''}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Folders Sidebar */}
        <div className="w-48 shrink-0">
          <h3 className="font-medium text-gray-900 mb-3">Folders</h3>
          <div className="space-y-1">
            <button
              onClick={() => setSelectedFolder(null)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                selectedFolder === null 
                  ? 'bg-[#00457f] text-white' 
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              <FolderOpen className="w-4 h-4" />
              <span>All Files</span>
            </button>
            {folders.map(folder => (
              <button
                key={folder.name}
                onClick={() => setSelectedFolder(folder.name)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                  selectedFolder === folder.name 
                    ? 'bg-[#00457f] text-white' 
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <div className="flex items-center gap-2">
                  <FolderOpen className="w-4 h-4" />
                  <span className="capitalize">{folder.name}</span>
                </div>
                <span className={`text-xs ${selectedFolder === folder.name ? 'text-white/70' : 'text-gray-400'}`}>
                  {folder.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Assets Grid/List */}
        <div className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#00457f]" />
            </div>
          ) : assets.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Image className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">
                  {searchQuery ? 'No files match your search' : 'No files in this folder'}
                </p>
                <Button onClick={() => fileInputRef.current?.click()}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Files
                </Button>
              </CardContent>
            </Card>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {assets.map(asset => (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  selected={selectedAssets.includes(asset.id)}
                  onSelect={() => toggleAssetSelection(asset.id)}
                  onCopy={() => handleCopyUrl(asset)}
                  onDelete={() => handleDelete(asset.id)}
                  onEdit={() => setEditingAsset(asset)}
                  onClick={() => handleSelectAsset(asset)}
                  copied={copiedId === asset.id}
                  selectMode={selectMode}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {assets.map(asset => (
                <AssetListItem
                  key={asset.id}
                  asset={asset}
                  selected={selectedAssets.includes(asset.id)}
                  onSelect={() => toggleAssetSelection(asset.id)}
                  onCopy={() => handleCopyUrl(asset)}
                  onDelete={() => handleDelete(asset.id)}
                  onEdit={() => setEditingAsset(asset)}
                  onClick={() => handleSelectAsset(asset)}
                  copied={copiedId === asset.id}
                  selectMode={selectMode}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Asset Modal */}
      {editingAsset && (
        <EditAssetModal
          asset={editingAsset}
          folders={folders}
          onSave={handleUpdateAsset}
          onClose={() => setEditingAsset(null)}
        />
      )}
    </div>
  );
};

const AssetCard = ({ asset, selected, onSelect, onCopy, onDelete, onEdit, onClick, copied, selectMode }) => {
  const isImage = asset.file_type === 'image';
  
  return (
    <div 
      className={`group relative bg-white border rounded-lg overflow-hidden transition-all hover:shadow-md ${
        selected ? 'ring-2 ring-[#00457f]' : ''
      } ${selectMode ? 'cursor-pointer' : ''}`}
      onClick={selectMode ? onClick : undefined}
    >
      {/* Selection checkbox */}
      {!selectMode && (
        <div className="absolute top-2 left-2 z-10">
          <input
            type="checkbox"
            checked={selected}
            onChange={onSelect}
            className="w-4 h-4 rounded border-gray-300 text-[#00457f] focus:ring-[#00457f]"
          />
        </div>
      )}

      {/* Preview */}
      <div className="aspect-square bg-gray-100 flex items-center justify-center">
        {isImage ? (
          <img 
            src={asset.url} 
            alt={asset.alt_text || asset.original_filename}
            className="w-full h-full object-cover"
          />
        ) : (
          <Video className="w-12 h-12 text-gray-400" />
        )}
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="text-sm font-medium text-gray-900 truncate" title={asset.original_filename}>
          {asset.original_filename}
        </p>
        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
          <span>{formatFileSize(asset.file_size)}</span>
          {asset.width && asset.height && (
            <span>• {asset.width}×{asset.height}</span>
          )}
        </div>
      </div>

      {/* Actions overlay */}
      {!selectMode && (
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onCopy(); }}
            className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
            title="Copy URL"
          >
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-2 bg-white rounded-full hover:bg-red-100 transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        </div>
      )}
    </div>
  );
};

const AssetListItem = ({ asset, selected, onSelect, onCopy, onDelete, onEdit, onClick, copied, selectMode }) => {
  const isImage = asset.file_type === 'image';
  
  return (
    <div 
      className={`flex items-center gap-4 p-3 bg-white border rounded-lg transition-all hover:shadow-sm ${
        selected ? 'ring-2 ring-[#00457f]' : ''
      } ${selectMode ? 'cursor-pointer' : ''}`}
      onClick={selectMode ? onClick : undefined}
    >
      {/* Checkbox */}
      {!selectMode && (
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          className="w-4 h-4 rounded border-gray-300 text-[#00457f] focus:ring-[#00457f]"
        />
      )}

      {/* Thumbnail */}
      <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center shrink-0">
        {isImage ? (
          <img 
            src={asset.url} 
            alt={asset.alt_text || asset.original_filename}
            className="w-full h-full object-cover rounded"
          />
        ) : (
          <Video className="w-6 h-6 text-gray-400" />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{asset.original_filename}</p>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span>{formatFileSize(asset.file_size)}</span>
          {asset.width && asset.height && <span>{asset.width}×{asset.height}</span>}
          <span className="capitalize">{asset.folder}</span>
          <span>{new Date(asset.uploaded_at).toLocaleDateString()}</span>
        </div>
      </div>

      {/* Actions */}
      {!selectMode && (
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onCopy(); }}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            title="Copy URL"
          >
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            title="Edit"
          >
            <Edit2 className="w-4 h-4 text-gray-500" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-2 hover:bg-red-50 rounded-md transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
        </div>
      )}
    </div>
  );
};

const EditAssetModal = ({ asset, folders, onSave, onClose }) => {
  const [altText, setAltText] = useState(asset.alt_text || '');
  const [folder, setFolder] = useState(asset.folder);
  const [tags, setTags] = useState(asset.tags?.join(', ') || '');

  const handleSave = () => {
    onSave(asset.id, {
      alt_text: altText,
      folder: folder,
      tags: tags
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-gray-900">Edit Asset</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Preview */}
          <div className="mb-6">
            {asset.file_type === 'image' ? (
              <img 
                src={asset.url} 
                alt={asset.original_filename}
                className="w-full h-48 object-contain bg-gray-100 rounded-lg"
              />
            ) : (
              <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                <Video className="w-12 h-12 text-gray-400" />
              </div>
            )}
            <p className="text-sm text-gray-500 mt-2 text-center">{asset.original_filename}</p>
          </div>

          {/* Form */}
          <div className="space-y-4">
            <div>
              <Label>Alt Text</Label>
              <Input
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                placeholder="Describe this image for accessibility"
              />
              <p className="text-xs text-gray-400 mt-1">Used for accessibility and SEO</p>
            </div>

            <div>
              <Label>Folder</Label>
              <select
                value={folder}
                onChange={(e) => setFolder(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                {folders.map(f => (
                  <option key={f.name} value={f.name} className="capitalize">
                    {f.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>Tags</Label>
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="tag1, tag2, tag3"
              />
              <p className="text-xs text-gray-400 mt-1">Comma-separated tags for search</p>
            </div>

            {/* URL display */}
            <div>
              <Label>URL</Label>
              <div className="flex gap-2">
                <Input
                  value={`${window.location.origin}${asset.url}`}
                  readOnly
                  className="bg-gray-50"
                />
                <Button
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}${asset.url}`);
                    toast.success('URL copied');
                  }}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[#00457f] hover:bg-[#003461]">
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaLibrary;
